import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Button } from "primereact/button";

import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import PaymentCertificateModal from "./PaymentCertificateModal";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";

import { paymentCertificateDeleteAPI, paymentCertificateGetDataApi } from "../../../../../Api/Project/DefaultServices/PaymentCertificateApi";
import { PaymentCertificates } from "../../../../../Controllers/Types/Project/DefaultService/PaymentCertificateInterface";
import { baseURL } from "../../../../../Api/ApiConstant";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { getAppCurrencyType } from "../../../../../Utils/Common";

interface Status {
    name: string;
    colorCode: string;
}
const PaymentCertificate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const location = useLocation();
    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const isShared: boolean = projectData.sharedBy !== null ? true : false;
    const currencySymbol = getAppCurrencyType();

    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const menuRefs = useRef<Record<number, Menu | null>>({});
    const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileUrl, setFileUrl] = useState<string>('');

    const [paymentCertificateData, setPaymentCertificateData] = useState<PaymentCertificates[]>([]);
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [selectedPayCerData, setSelectedPayCerData] = useState<PaymentCertificates | null>(null);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermission_add_payment_certificate = useCanEdit('Payment Certificate', 'add_payment_certificate');
    const isPermission_edit_payment_certificate = useCanEdit('Payment Certificate', 'edit_payment_certificate');
    const isPermission_delete_payment_certificate = useCanEdit('Payment Certificate', 'delete_payment_certificate');

    useEffect(() => {
        paymentCertificateDataGet();
    }, [serviceId]);

    const statuses: Status[] = [
        {
            name: "Paid",
            colorCode: '#B9F6CA'
        },
        {
            name: "Unpaid",
            colorCode: '#FFE2B2'
        }
    ];

    /* ********************************* get data list ************************************************** */
    const paymentCertificateDataGet = async () => {
        if (serviceId) {
            setDataLoading(true);
            const data = await paymentCertificateGetDataApi(Number(serviceId));
            setDataLoading(false);
            setInitialDataLoading(false);

            if (data.status) {
                setPaymentCertificateData(data.data || []);
            } else {
                setErrorMessage(`An error occurred while retrieving ${(projectData.feature_name).toLowerCase()} data.`);
            }
        }
    }
    /* ********************************* get data list ************************************************** */


    /* *************************** For table action menu ************************** */
    const tableActionMenuItems: MenuItem[] = [
        ...(isPermission_edit_payment_certificate
            ? [{
                label: 'Edit',
                command: () => {
                    if (selectedPayCerData !== null) {
                        handleEditDetails(selectedPayCerData);
                    }
                }
            },]
            : []
        ),

        ...(isPermission_delete_payment_certificate
            ? [{
                label: 'Delete',
                command: () => {
                    if (selectedPayCerData !== null) {
                        handleDeleteData(selectedPayCerData?.id!);
                    }
                }
            }]
            : []
        ),
    ];

    const tableActionMenuToggle = (e: React.MouseEvent<HTMLButtonElement>, payCertificate: PaymentCertificates) => {
        e.stopPropagation();
        setSelectedPayCerData(payCertificate);

        if (openMenuId === payCertificate?.id) {
            menuRefs.current[payCertificate?.id]?.hide(e);
            setOpenMenuId(null);
        } else {
            if (openMenuId !== null && menuRefs.current[openMenuId]) {
                menuRefs.current[openMenuId]?.hide(e);
            }
            menuRefs.current[payCertificate?.id!]?.toggle(e);
            setOpenMenuId(payCertificate?.id!);
        }
    }
    /* *************************************************************************** */


    /* ************************** For payment certificate ************************ */
    const handleAddDetails = () => {
        setShowDetailModal(true);
        setDataState('Add');
    }

    const handleEditDetails = (payCerData: PaymentCertificates) => {
        setShowDetailModal(true);
        setDataState('Edit');
        setOpenMenuId(null);
        setSelectedPayCerData(payCerData);
    }

    /* *************************************************  Delete ***************************************** */
    const handleDeleteData = (payCerId: number): void => {
        setOpenMenuId(null);
        confirmDialog({
            message: 'Are you sure you want to delete this document data?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteData(payCerId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const deleteData = async (payCerId: number) => {
        const data = await paymentCertificateDeleteAPI(payCerId);

        if (data.statusCode === 200) {
            paymentCertificateDataGet();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Document data deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting document data.',
                    life: 3000
                });
            }
        }
    }
    /* *************************************************  Delete ***************************************** */


    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedPayCerData(null);
    }
    /* *************************************************************************** */


    /* ***************************** For view file ****************************** */
    const handleViewDocument = (document: string) => {
        setFileUrl(document);
        setShowFileViewModal(true);
    }

    const handleCloseDocumentViewModal = () => {
        setShowFileViewModal(false);
        setFileUrl('');
    };
    /* *************************************************************************** */


    /* ***************************** For table view ****************************** */
    const getRowClassName = (data: PaymentCertificates, options: { [key: string]: any }) => {
        const paymentStatus = data?.status;

        if (paymentStatus === 'Paid') {
            return 'project_data_row completed';
        } else if (paymentStatus === 'Unpaid') {
            return 'project_data_row ongoing';
        } else {
            return 'project_data_row';
        }
    };

    const fileViewBody = (rowData: PaymentCertificates) => {
        return (
            <span className="d-flex justify-content-center">
                {rowData.path !== null ? (
                    <button className="doc_view_btn p-ripple"
                        onClick={() => handleViewDocument(baseURL + rowData.path!)}>
                        <Ripple />
                        <i className="bi bi-file-earmark-pdf"></i>
                    </button>
                ) : (
                    <span className="text-secondary text-center">--</span>
                )}
            </span>
        )
    }

    const tableActionBody = (rowData: PaymentCertificates) => {
        return (
            <React.Fragment>
                <Button
                    ref={(el) => (buttonRefs.current[rowData?.id as number] = el as HTMLButtonElement | null)}
                    key={`menu_toggle_button_${rowData?.id}`}
                    id={`menu_toggle_button_${rowData?.id}`}
                    icon="bi bi-three-dots-vertical"
                    className="is_btn action_trigger_btn p-ripple"
                    onClick={(e) => tableActionMenuToggle(e, rowData)}
                    aria-controls={`popup_menu_${rowData?.id}`}
                    aria-haspopup
                />

                <Menu
                    ref={(el) => (menuRefs.current[rowData?.id!] = el)}
                    id={`popup_menu_${rowData?.id}`}
                    key={`popup_menu_${rowData?.id}`}
                    model={tableActionMenuItems}
                    popup
                    appendTo={document.body}
                    className="service_action_menu"
                />
            </React.Fragment>
        )
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId !== null) {
                const menuElement = menuRefs.current[openMenuId]?.getElement();
                const buttonElement = buttonRefs.current[openMenuId];

                if (
                    menuElement &&
                    !menuElement.contains(event.target as Node) &&
                    buttonElement &&
                    !buttonElement.contains(event.target as Node)
                ) {
                    const syntheticEvent = { stopPropagation: () => { }, preventDefault: () => { } } as React.SyntheticEvent;
                    menuRefs.current[openMenuId]?.hide(syntheticEvent);
                    setOpenMenuId(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuId]);
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="status_lable_container">
                                {paymentCertificateData?.length > 0 && (
                                    <div className="status_lable_area">
                                        {statuses?.map((status, index) => (
                                            <div key={index} className="status_lable">
                                                <span style={{ backgroundColor: status.colorCode }}></span>
                                                <h6>{status.name}</h6>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {isPermission_add_payment_certificate && !isShared && (
                                <div className="page_action_area">
                                    <button className="add_data_link_btn p-ripple" onClick={handleAddDetails}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add certificate</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section">
                            {paymentCertificateData?.length > 0 ? (
                                <DataTable
                                    value={paymentCertificateData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    emptyMessage="No data available"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    rowClassName={getRowClassName}
                                    loading={dataLoading}
                                    scrollable
                                >
                                    <Column
                                        header="Cer No."
                                        headerClassName="text_purple"
                                        field="certificate_no"
                                        body={(rowData: PaymentCertificates) => (
                                            <span className="text_bold text_no_wrap">
                                                {rowData.certificate_no ? rowData.certificate_no : '---'}
                                            </span>
                                        )}
                                        style={{ width: '8%' }}
                                    >
                                    </Column>

                                    <Column
                                        header="Total"
                                        alignHeader={'right'}
                                        headerClassName="text_purple"
                                        field="total_amount"
                                        body={(rowData) => (
                                            <span className="text_no_wrap text_semi_bold text_bold flex_end">
                                                {currencySymbol}&nbsp;{rowData.total_amount ?
                                                    parseFloat(rowData.total_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '---'
                                                }
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    >
                                    </Column>

                                    <Column
                                        header="Retention"
                                        headerClassName="text_purple"
                                        alignHeader={'right'}
                                        field="retention_amount"
                                        body={(rowData) => (
                                            <span className="text_no_wrap flex_end">
                                                {currencySymbol}&nbsp;{rowData.retention_amount ?
                                                    parseFloat(rowData.retention_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '---'
                                                }
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    >
                                    </Column>

                                    <Column
                                        header="VAT"
                                        headerClassName="text_purple"
                                        alignHeader={'right'}
                                        field="vat"
                                        body={(rowData) => (
                                            <span className="text_no_wrap flex_end">
                                                {currencySymbol}&nbsp;{rowData.vat ?
                                                    parseFloat(rowData.vat).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '---'
                                                }
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    >
                                    </Column>

                                    <Column
                                        header="Certificate amount"
                                        headerClassName="text_purple"
                                        alignHeader={'right'}
                                        field="certificate_amount"
                                        body={(rowData) => (
                                            <span className="text_no_wrap flex_end">
                                                {currencySymbol}&nbsp;{rowData.certificate_amount ?
                                                    parseFloat(rowData.certificate_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                                    : '---'
                                                }
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    >
                                    </Column>

                                    <Column
                                        header="Status"
                                        alignHeader={'center'}
                                        headerClassName="text_purple"
                                        field="status"
                                        body={(rowData: PaymentCertificates) => (
                                            <span className="text_no_wrap text_semi_bold flex_center">
                                                {rowData.status ? rowData.status : '---'}
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    >
                                    </Column>

                                    <Column
                                        header="Doc"
                                        headerClassName="text_purple"
                                        alignHeader={"center"}
                                        body={fileViewBody}
                                        style={{ width: '12%' }}
                                    >
                                    </Column>

                                    {!isShared && (isPermission_delete_payment_certificate || isPermission_edit_payment_certificate) && (
                                        <Column
                                            body={tableActionBody}
                                            style={{ width: '5%' }}
                                            frozen
                                            alignFrozen="right"
                                        >
                                        </Column>
                                    )}
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${(projectData.feature_name).toLowerCase()} document added. ${!isShared && isPermission_add_payment_certificate ? 'To add the Document click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div >


            {/*  Certificate modal */}
            <PaymentCertificateModal
                isOpen={showDetailModal}
                dataState={dataState}
                onClose={handleCloseDetailModal}
                onFetchData={paymentCertificateDataGet}
                toast={toast}
                serviceId={serviceId}
                selectedPayCerData={selectedPayCerData!}
            />


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={fileUrl!}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default PaymentCertificate;